import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { sessionKeys, sessionQuery } from "@/features/auth/api/session.queries";
import type { Session } from "@/features/auth/api/session.api";
import { onSessionExpired, resetSessionExpiry, setSessionRenewal } from "@/lib/api/client";
import { ApiError } from "@altek/ui/lib/problem";
import { permissionSetFrom, type PermissionSet } from "@/lib/permissions/permissions";

/**
 * Who is using the application, and what they may do.
 *
 * The session is a QUERY, not provider state. That is the whole design: the permission set
 * lives in the same cache as everything else, a role change invalidates one key, and nothing
 * has to be mirrored into `useState` where it could go stale. The provider is a thin reader
 * over that query so components get a hook instead of a query key.
 *
 * THREE STATES, and conflating any two of them produces a visible bug:
 *
 *   `loading`       — nobody has asked yet. Render the boot screen, not the sign-in form: a
 *                     sign-in form flashed at an already-authenticated user on every reload is
 *                     the most common symptom of getting this wrong.
 *   `unauthenticated` — asked, answered 401. Sign-in.
 *   `authenticated`   — asked, answered. Everything else.
 */

interface AuthContextValue {
  status: "loading" | "authenticated" | "unauthenticated";
  session: Session | undefined;
  permissions: PermissionSet;
  /** Clears every cached query. Called after sign-out, and on an expired session. */
  reset: () => Promise<void>;
  /** Re-reads the session — after a sign-in, or after the caller's own role changed. */
  refresh: () => Promise<void>;
  /**
   * «expired» when a signed-in session ended by itself (the 30-day sign-in ran out, it was
   * revoked) rather than by «Çıkış yap» — sign-in then says so instead of opening silently.
   */
  endedBy: "expired" | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const query = useQuery(sessionQuery());
  const [endedBy, setEndedBy] = useState<"expired" | null>(null);

  /**
   * Drops the departing person's data and records that nobody is signed in.
   *
   * NOT `queryClient.clear()`, which is what this used to be and was the bug.
   *
   * Two things have to happen at once and `clear()` only does one of them. Every OTHER cached
   * key must go — invalidating instead would refetch them with the NEXT person's cookie, and
   * on a shared warehouse terminal the new user would watch the previous one's rows sit on
   * screen while their own loaded. But the SESSION query must survive as an entry, because
   * this provider's observer is subscribed to it: remove the entry and React Query leaves the
   * observer holding its last result, so the application still reported `authenticated` after
   * sign-out. `LoginPage` sends an authenticated visitor to `/my-panel`, so signing out bounced
   * straight back in, and the sign-in that followed invalidated a key that no longer existed
   * and refetched nothing. Reloading the page rebuilt the provider and hid all of it — the
   * signature of the whole "works after a refresh" bug class.
   *
   * So: remove everything else, and write `null` — the signed-out value — into the session
   * query, where the mounted observer can actually see it.
   */
  const signOutLocally = useCallback(() => {
    queryClient.removeQueries({
      predicate: (cached) => cached.queryKey[0] !== sessionKeys.all[0],
    });
    queryClient.setQueryData(sessionKeys.current(), null);
  }, [queryClient]);

  const reset = useCallback(async () => {
    signOutLocally();
    resetSessionExpiry();
    setEndedBy(null);
    await Promise.resolve();
  }, [signOutLocally]);

  const refresh = useCallback(async () => {
    resetSessionExpiry();
    setEndedBy(null);
    await queryClient.invalidateQueries({ queryKey: sessionKeys.all });
  }, [queryClient]);

  /**
   * KİMLİK DEĞİŞİNCE ÖNBELLEK BOŞALIR — çıkış yapmakla aynı sebep, tek fark oturumun sürmesi.
   *
   * Uygulamada oturumu kapatmadan kimlik değiştiren ÜÇ yol var ve üçü de yalnız oturum
   * satırını tazeliyordu: destek oturumuna geçmek, ondan dönmek, ve müşteri değiştirmek.
   * Geri kalan her anahtar — sohbet listesi, okunmamış sayacı, bildirimler, onay kutusu —
   * ÖNCEKİ kimliğin cevabını tutmaya devam ediyordu, çünkü hiçbiri bayatlamamıştı. Sonuç:
   * ekranın üst çubuğu yeni kişiyi, zil ile sohbet yuvası eski kişiyi gösteriyordu.
   *
   * `invalidate` değil `remove`: tazelemek doğru cevabı İSTER ama eski satırlar yeni cevap
   * gelene kadar ekranda kalır — yani bir kişinin konuşma başlıkları, adı değişmiş bir çubuğun
   * altında bir süre durur. Bu, `signOutLocally`'nin çözdüğü sorunun aynısı; oradaki gerekçe
   * burada da geçerli.
   *
   * ÜÇLÜ ANAHTAR: kullanıcı, destek oturumunu açan yönetici, ve müşteri. Yalnız kullanıcıya
   * bakmak müşteri değişimini kaçırırdı (aynı kişi, başka şirket, başka menü); yalnız müşteriye
   * bakmak destek oturumunu kaçırırdı. İlk yüklemede hiçbir şey silinmiyor — `before === null`
   * o durum, ve orada silinecek bir önceki kimlik yok.
   */
  const session = query.data ?? undefined;
  const identity =
    session == null
      ? null
      : [
          session.user.id,
          session.impersonation?.adminUserId ?? "",
          session.tenant?.current?.id ?? "",
        ].join("|");
  const previousIdentity = useRef<string | null>(null);

  /**
   * Destek oturumunda sessiz yenileme KAPALI (`client.ts` › silent renewal): yenileme çerezi
   * yöneticinin kendisine ait, yenilemek isteği onun kimliğiyle tekrarlamak olurdu.
   */
  const impersonating = Boolean(session?.impersonation);
  useEffect(() => {
    setSessionRenewal(!impersonating);
  }, [impersonating]);

  useEffect(() => {
    const before = previousIdentity.current;
    previousIdentity.current = identity;
    if (identity === null || before === null || before === identity) return;
    queryClient.removeQueries({
      predicate: (cached) => cached.queryKey[0] !== sessionKeys.all[0],
    });
  }, [identity, queryClient]);

  /**
   * The API client announces an expired session once, however many requests failed together.
   * Handled here rather than in each caller so a dashboard's six simultaneous 401s produce one
   * transition instead of six.
   */
  useEffect(() => {
    // Same transition as an explicit sign-out, minus `resetSessionExpiry()`: re-arming the
    // guard here would let the other five simultaneous 401s announce again. The next
    // successful sign-in re-arms it through `refresh()`.
    return onSessionExpired(() => {
      // Only a session that existed can «expire»: a cold visit to sign-in also answers 401.
      const hadSession = queryClient.getQueryData(sessionKeys.current()) != null;
      signOutLocally();
      if (hadSession) setEndedBy("expired");
    });
  }, [queryClient, signOutLocally]);

  const value = useMemo<AuthContextValue>(() => {
    const isUnauthenticated =
      query.error instanceof ApiError && query.error.status === 401;

    return {
      status: query.isPending
        ? "loading"
        : isUnauthenticated || !query.data
          ? "unauthenticated"
          : "authenticated",
      // `null` is the signed-out value; the context speaks in `undefined`.
      session: query.data ?? undefined,
      permissions: permissionSetFrom(query.data ?? undefined),
      reset,
      refresh,
      endedBy,
    };
  }, [query.isPending, query.error, query.data, reset, refresh, endedBy]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth, AuthProvider içinde kullanılmalı.");
  return context;
}

/** The permission set alone, for the common case. */
export function usePermissions(): PermissionSet {
  return useAuth().permissions;
}

/** The signed-in person, or null. */
export function useCurrentUser(): Session["user"] | null {
  return useAuth().session?.user ?? null;
}
